// web/src/pages/admin/PaymentMethodsPage.tsx
//
// I-11 — Admin CRUD for payment methods (`/api/admin/payment-methods`).
//
// No foundation rows — all seeded methods are user-editable.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  listPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
  type PaymentMethodDto,
} from "@/lib/invoices-payments";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { EntityForm } from "@/components/entity-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const schema = z.object({
  description: z.string().min(1).max(100),
  pymtCode: z.string().max(20).nullable(),
  clientInfoMsg: z.string().max(500).nullable(),
  displayOnClientSite: z.boolean(),
  acceptedOnInvoicePdf: z.boolean(),
  autoOnly: z.boolean(),
  autoReconcile: z.boolean(),
  sortOrder: z.number().int(),
});
type Form = z.infer<typeof schema>;

export function PaymentMethodsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PaymentMethodDto | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ["payment-methods"],
    queryFn: () => listPaymentMethods(),
  });

  const upsert = useMutation({
    mutationFn: (form: Form) =>
      editing
        ? updatePaymentMethod(editing.id, {
            description: form.description,
            pymtCode: form.pymtCode,
            clientInfoMsg: form.clientInfoMsg,
            displayOnClientSite: form.displayOnClientSite,
            acceptedOnInvoicePdf: form.acceptedOnInvoicePdf,
            autoOnly: form.autoOnly,
            autoReconcile: form.autoReconcile,
            sortOrder: form.sortOrder,
          })
        : createPaymentMethod({
            description: form.description,
            pymtCode: form.pymtCode,
            clientInfoMsg: form.clientInfoMsg,
            displayOnClientSite: form.displayOnClientSite,
            acceptedOnInvoicePdf: form.acceptedOnInvoicePdf,
            autoOnly: form.autoOnly,
            autoReconcile: form.autoReconcile,
            sortOrder: form.sortOrder,
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["payment-methods"] });
      setEditing(null);
      setCreating(false);
    },
  });

  const del = useMutation({
    mutationFn: (id: number) => deletePaymentMethod(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["payment-methods"] }),
  });

  const cols: DataGridColumn<PaymentMethodDto>[] = [
    { id: "id",   header: "ID",          accessor: (r) => r.id, align: "right" },
    { id: "desc", header: "Description", accessor: (r) => r.description },
    { id: "code", header: "Code",        accessor: (r) => r.pymtCode ?? "—" },
    { id: "site", header: "Client site", accessor: (r) => r.displayOnClientSite ? "Yes" : "—" },
    { id: "pdf",  header: "Invoice PDF", accessor: (r) => r.acceptedOnInvoicePdf ? "Yes" : "—" },
    { id: "auto", header: "Auto-only / reconcile", accessor: (r) => `${r.autoOnly ? "AO" : "—"} / ${r.autoReconcile ? "AR" : "—"}` },
    { id: "sort", header: "Sort",        accessor: (r) => r.sortOrder, align: "right" },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Edit ${r.description}`}
            onClick={() => setEditing(r)}
          >
            Edit
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Delete ${r.description}`}
            disabled={del.isPending}
            onClick={() => del.mutate(r.id)}
          >
            Delete
          </Button>
        </div>
      ),
      align: "right",
    },
  ];

  const drawerOpen = creating || editing !== null;
  const defaultValues: Form = editing
    ? {
        description: editing.description,
        pymtCode: editing.pymtCode,
        clientInfoMsg: editing.clientInfoMsg,
        displayOnClientSite: editing.displayOnClientSite,
        acceptedOnInvoicePdf: editing.acceptedOnInvoicePdf,
        autoOnly: editing.autoOnly,
        autoReconcile: editing.autoReconcile,
        sortOrder: editing.sortOrder,
      }
    : {
        description: "",
        pymtCode: null,
        clientInfoMsg: null,
        displayOnClientSite: false,
        acceptedOnInvoicePdf: false,
        autoOnly: false,
        autoReconcile: false,
        sortOrder: 0,
      };

  return (
    <ListPage
      title="Payment methods"
      primaryAction={
        <IntentButton onClick={() => setCreating(true)}>+ New</IntentButton>
      }
    >
      <DataGrid
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        totalRows={list.data?.length ?? 0}
        pageIndex={0}
        pageSize={Math.max(50, list.data?.length ?? 0)}
        loading={list.isLoading}
        onPageChange={() => undefined}
      />
      <DetailsDrawer
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={editing ? "Edit payment method" : "New payment method"}
      >
        <EntityForm<Form>
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              <div>
                <Label htmlFor="pm-desc">Description</Label>
                <Input id="pm-desc" {...form.register("description")} />
              </div>
              <div>
                <Label htmlFor="pm-code">Code</Label>
                <Input
                  id="pm-code"
                  {...form.register("pymtCode", {
                    setValueAs: (v: string) => (v === "" ? null : v),
                  })}
                />
              </div>
              <div>
                <Label htmlFor="pm-msg">Client info message</Label>
                <Input
                  id="pm-msg"
                  {...form.register("clientInfoMsg", {
                    setValueAs: (v: string) => (v === "" ? null : v),
                  })}
                />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...form.register("displayOnClientSite")} /> Display on client site
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...form.register("acceptedOnInvoicePdf")} /> Accepted on invoice PDF
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...form.register("autoOnly")} /> Auto-only
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...form.register("autoReconcile")} /> Auto-reconcile
              </label>
              <div>
                <Label htmlFor="pm-sort">Sort order</Label>
                <Input
                  id="pm-sort"
                  type="number"
                  {...form.register("sortOrder", {
                    setValueAs: (v: string) => (v === "" ? 0 : Number(v)),
                  })}
                />
              </div>
              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailsDrawer>
    </ListPage>
  );
}
